// Translation layer between the granular permission catalog (server-side
// source of truth) and the page-card UI used in Roles & Permissions.
//
// Backend remains untouched: it stores and enforces individual permission
// names. The UI groups those names into "what can this role do on each
// page?" with a single 4-level selector (None / View / Edit / Full).
//
// Two pure functions do the round-trip:
//   permissionsToCardLevels(set)  -> { cardId: level }
//   cardLevelsToPermissions(map)  -> string[]   (the bundle to PUT)
//
// `view_admin_panel` and `view_settings_panel` are auto-derived: if any
// card in the admin/settings section is at a level other than "none", the
// containing panel-view permission is granted automatically. This keeps
// the admin from having to remember the meta-permission.

import {
  PERMISSION_NAMES,
  LEGACY_PRESET_PERMISSIONS,
  type PermissionName,
  type PresetRoleKey,
} from "./permissions";

export type PageCardLevel = "none" | "view" | "edit" | "full";

export type PageCardSection = "app" | "admin" | "settings";

export interface PageCardDef {
  id: string;
  label: string;
  description: string;
  section: PageCardSection;
  /** Levels offered for this card, in the order they should render. */
  availableLevels: PageCardLevel[];
  /** Permission bundle for each level. Must be cumulative — higher levels
   *  include the lower level's permissions. None is always []. */
  levelToPerms: Record<PageCardLevel, PermissionName[]>;
  /** Optional per-card label overrides for the level buttons. When omitted
   *  the UI falls back to the default labels (None / View / Edit / Full
   *  Authority). Cards whose levels don't match the access semantics
   *  (e.g. Summary uses "View own / View own + dept / View all") supply
   *  their own labels here. */
  levelLabels?: Partial<Record<PageCardLevel, string>>;
}

export const PAGE_CARDS: ReadonlyArray<PageCardDef> = [
  // ─── App Areas ─────────────────────────────────────────────────────────
  {
    id: "summary",
    label: "Summary",
    description:
      "Personal landing page with the user's open items. The four levels gate which items the user can see in the Summary, Action Item Register, and Updates page lists. None hides every item (page still reachable but empty). View own = items where the user is owner, current holder, or has a sub-assignment. View own + department = adds peers in the same department, but only if the user's team-member record has is_department_head=true. View all = bypasses the filter and shows every item across the organisation.",
    section: "app",
    availableLevels: ["none", "view", "edit", "full"],
    levelLabels: {
      none: "None",
      view: "View own",
      edit: "View own + department",
      full: "View all",
    },
    levelToPerms: {
      none: [],
      view: ["summary_view_own"],
      edit: ["summary_view_own", "summary_view_department"],
      full: ["summary_view_own", "summary_view_department", "summary_view_all"],
    },
  },
  {
    id: "action-items",
    label: "Action Item Register",
    description:
      "The main register and detail pages. View is read-only; Edit covers everyday work (create, edit, sign-off, return, export reports); Full adds soft-delete.",
    section: "app",
    availableLevels: ["view", "edit", "full"],
    levelToPerms: {
      none: [],
      view: [],
      edit: [
        "create_action_item",
        "edit_action_item",
        "signoff_action_item",
        "return_action_item",
        "view_reports",
      ],
      full: [
        "create_action_item",
        "edit_action_item",
        "signoff_action_item",
        "return_action_item",
        "view_reports",
        "delete_action_item",
      ],
    },
  },
  {
    id: "md-meetings",
    label: "MD Meetings",
    description:
      "The MD review queue and Awaiting-Dispatch view. None hides it from navigation entirely. SDF MD / PMO / Board Secretary identity bindings layer on top — those remain unaffected by this matrix.",
    section: "app",
    availableLevels: ["none", "view", "edit", "full"],
    levelToPerms: {
      none: [],
      view: ["view_md_meetings", "view_awaiting_dispatch"],
      edit: ["view_md_meetings", "view_awaiting_dispatch", "act_on_md_review"],
      full: [
        "view_md_meetings",
        "view_awaiting_dispatch",
        "act_on_md_review",
        "mark_dispatched",
      ],
    },
  },
  {
    id: "escalations",
    label: "Stalled-task Escalations",
    description:
      "Inbox for senior reviewers (typically C-suite) to confirm or dismiss 'dropped-the-ball' escalations raised by other users. Holders of this card at Full can also flag a stalled reassignment as Insufficient Progress directly without going through the escalation flow.",
    section: "app",
    availableLevels: ["none", "full"],
    levelToPerms: {
      none: [],
      view: ["confirm_dropped_the_ball"],
      edit: ["confirm_dropped_the_ball"],
      full: ["confirm_dropped_the_ball"],
    },
  },
  {
    id: "hr-dossier",
    label: "HR Dossier",
    description:
      "Overdue-by-holder dossier and HR review tabs. None hides the page; View shows the Overdue Dossier only; Edit adds the MD Review History tab (per-item reschedule chain and SLA verdict for items sent to SDF MD); Full adds the Employee Dossier tab (per-person performance report suitable for an annual review packet — verdict per item, on-time vs late holding periods, drop count).",
    section: "app",
    availableLevels: ["none", "view", "edit", "full"],
    levelToPerms: {
      none: [],
      view: ["view_hr_dossier"],
      edit: ["view_hr_dossier", "view_md_review_history"],
      full: ["view_hr_dossier", "view_md_review_history", "view_employee_dossier"],
    },
  },

  // ─── Admin Page Tabs ──────────────────────────────────────────────────
  // Granting any non-None level here auto-grants `view_admin_panel`.
  {
    id: "kpi-dashboard",
    label: "KPI Dashboard",
    description:
      "Per-person KPI tile inside the Admin page. View-only — there's nothing destructive here.",
    section: "admin",
    availableLevels: ["none", "view"],
    levelToPerms: {
      none: [],
      view: ["view_kpi_dashboard"],
      edit: ["view_kpi_dashboard"],
      full: ["view_kpi_dashboard"],
    },
  },
  {
    id: "governance-config",
    label: "Governance Configuration",
    description:
      "Authority lanes, actors, reference lists, workflow rules, field permissions, transition permissions. Full also enables overriding/revising due dates after registration.",
    section: "admin",
    availableLevels: ["none", "full"],
    levelToPerms: {
      none: [],
      view: ["manage_governance", "revise_due_date"],
      edit: ["manage_governance", "revise_due_date"],
      full: ["manage_governance", "revise_due_date"],
    },
  },
  {
    id: "team-members",
    label: "Team Members",
    description:
      "Internal team-member directory used for assignee dropdowns. Edit allows create/update; Full adds permanent deletion.",
    section: "admin",
    availableLevels: ["none", "edit", "full"],
    levelToPerms: {
      none: [],
      view: ["manage_team_members"],
      edit: ["manage_team_members"],
      full: ["manage_team_members", "delete_team_members"],
    },
  },
  {
    id: "user-accounts",
    label: "User Accounts",
    description:
      "Login accounts and role assignments. Edit allows create/update; Full adds permanent deletion.",
    section: "admin",
    availableLevels: ["none", "edit", "full"],
    levelToPerms: {
      none: [],
      view: ["manage_users"],
      edit: ["manage_users"],
      full: ["manage_users", "delete_users"],
    },
  },
  {
    id: "roles-permissions",
    label: "Roles & Permissions",
    description:
      "This very tab. Full enables editing the authorization matrix and viewing the role-change audit log.",
    section: "admin",
    availableLevels: ["none", "full"],
    levelToPerms: {
      none: [],
      view: ["manage_roles_permissions"],
      edit: ["manage_roles_permissions"],
      full: ["manage_roles_permissions"],
    },
  },
  {
    id: "reminder-schedule",
    label: "Reminder Schedule",
    description:
      "Pre-due email reminder offsets (default 14 / 7 / 1 days). Past-due cadence is fixed in code (3 / 6 / 9 / 12 days). Includes the reminder send log.",
    section: "admin",
    availableLevels: ["none", "full"],
    levelToPerms: {
      none: [],
      view: ["manage_reminder_schedule"],
      edit: ["manage_reminder_schedule"],
      full: ["manage_reminder_schedule"],
    },
  },
  {
    id: "deleted-items",
    label: "Deleted Items",
    description:
      "Recycle bin for soft-deleted action items. View shows the list; Full enables restore and permanent purge.",
    section: "admin",
    availableLevels: ["none", "view", "full"],
    levelToPerms: {
      none: [],
      view: ["view_deleted_items"],
      edit: ["view_deleted_items"],
      full: ["view_deleted_items", "manage_deleted_items", "restore_action_item"],
    },
  },

  // ─── Settings Page Tabs ───────────────────────────────────────────────
  // Granting any non-None level here auto-grants `view_settings_panel`.
  {
    id: "branding",
    label: "Branding",
    description: "Company name, department, logo, base URL.",
    section: "settings",
    availableLevels: ["none", "full"],
    levelToPerms: {
      none: [],
      view: ["manage_branding"],
      edit: ["manage_branding"],
      full: ["manage_branding"],
    },
  },
  {
    id: "smtp",
    label: "Email / SMTP",
    description: "Outbound email server configuration and templates.",
    section: "settings",
    availableLevels: ["none", "full"],
    levelToPerms: {
      none: [],
      view: ["manage_smtp"],
      edit: ["manage_smtp"],
      full: ["manage_smtp"],
    },
  },
  {
    id: "api-keys",
    label: "API Keys",
    description: "Agent API keys for external LLM/AI integrations.",
    section: "settings",
    availableLevels: ["none", "full"],
    levelToPerms: {
      none: [],
      view: ["manage_api_keys"],
      edit: ["manage_api_keys"],
      full: ["manage_api_keys"],
    },
  },
  {
    id: "backup",
    label: "Backup & Distribution",
    description: "Database backups and compiled-package downloads.",
    section: "settings",
    availableLevels: ["none", "full"],
    levelToPerms: {
      none: [],
      view: ["manage_backup"],
      edit: ["manage_backup"],
      full: ["manage_backup"],
    },
  },
  {
    id: "live-refresh",
    label: "Live Refresh & Performance",
    description:
      "Data freshness, polling intervals, apply method, emergency pause.",
    section: "settings",
    availableLevels: ["none", "full"],
    levelToPerms: {
      none: [],
      view: ["manage_runtime_config"],
      edit: ["manage_runtime_config"],
      full: ["manage_runtime_config"],
    },
  },
  {
    id: "system-logs",
    label: "System Diagnostics Log",
    description:
      "Centralized error and event viewer for IT diagnostics. Covers email delivery, authentication, database, scheduler, API, and system events. Downloadable as CSV/XLSX.",
    section: "settings",
    availableLevels: ["none", "view"],
    levelToPerms: {
      none: [],
      view: ["view_system_logs"],
      edit: ["view_system_logs"],
      full: ["view_system_logs"],
    },
  },
] as const;

export const SECTION_LABELS: Record<PageCardSection, string> = {
  app: "App Areas",
  admin: "Admin Page Tabs",
  settings: "Settings Page Tabs",
};

export const SECTION_NOTES: Record<PageCardSection, string> = {
  app: "Top-level pages every signed-in user navigates between.",
  admin:
    "Tabs inside the Admin page. Granting any of these automatically gives the role access to the Admin nav-link.",
  settings:
    "Tabs inside the System Settings page. Granting any of these automatically gives the role access to the Settings nav-link.",
};

/** Pages that are visible to every authenticated user regardless of role.
 *  Listed for the admin's reference in the UI — they are not configurable. */
export const ALWAYS_VISIBLE_DESTINATIONS: ReadonlyArray<{ label: string; description: string }> = [
  { label: "Origin Trace", description: "Read-only origin-by-reference search." },
  { label: "Register (page)", description: "The Action Item Register page itself is always reachable; what a role can do there is set by the Action Item Register card above." },
  { label: "Updates", description: "Recent progress updates feed." },
  { label: "Meeting Tasks", description: "Personal task list for meetings the user owns." },
  { label: "User Guide", description: "In-app documentation." },
];

// Permissions that are auto-derived from non-None section state.
// They are intentionally NOT bound to any individual card — the UI never
// shows a checkbox for them — and the round-trip discards them on the way
// in and re-derives them on the way out.
const AUTO_GRANTS: ReadonlyArray<{ section: PageCardSection; perm: PermissionName }> = [
  { section: "admin", perm: "view_admin_panel" },
  { section: "settings", perm: "view_settings_panel" },
];
const AUTO_GRANT_PERMS = new Set<string>(AUTO_GRANTS.map((g) => g.perm));

/** Map a saved permission set to the level each card should display. */
export function permissionsToCardLevels(perms: ReadonlySet<string>): Record<string, PageCardLevel> {
  const out: Record<string, PageCardLevel> = {};
  for (const card of PAGE_CARDS) {
    let chosen: PageCardLevel = "none";
    // Walk the card's available levels from highest available down to "none"
    // and pick the first whose bundle is fully present in the saved set.
    // Because levels are cumulative this is well-defined.
    const ordered: PageCardLevel[] = ["full", "edit", "view", "none"].filter((l) =>
      card.availableLevels.includes(l as PageCardLevel),
    ) as PageCardLevel[];
    for (const lvl of ordered) {
      const required = card.levelToPerms[lvl];
      if (required.length === 0 && lvl === "none") {
        chosen = "none";
        break;
      }
      if (required.length === 0) continue;
      const allPresent = required.every((p) => perms.has(p));
      if (allPresent) {
        chosen = lvl;
        break;
      }
    }
    out[card.id] = chosen;
  }
  return out;
}

/** Map per-card levels back to the permission bundle to PUT. Auto-grants
 *  `view_admin_panel` / `view_settings_panel` based on section state. */
export function cardLevelsToPermissions(levels: Record<string, PageCardLevel>): string[] {
  const out = new Set<string>();
  for (const card of PAGE_CARDS) {
    const lvl = levels[card.id] ?? "none";
    for (const p of card.levelToPerms[lvl]) out.add(p);
  }
  for (const grant of AUTO_GRANTS) {
    const anyOn = PAGE_CARDS.some(
      (c) => c.section === grant.section && (levels[c.id] ?? "none") !== "none",
    );
    if (anyOn) out.add(grant.perm);
  }
  return Array.from(out).sort();
}

/** Plain-English one-liner for a role's permission set, used in the role
 *  list rail. Returns something like
 *  "Full: HR Dossier · Edit: Action Items · View: KPIs". */
export function summarizeRolePermissions(perms: ReadonlySet<string>): string {
  // Heuristic short-circuits for the two extremes.
  if (perms.size === 0) return "No admin access (member-level).";
  const total = PERMISSION_NAMES.filter((n) => !AUTO_GRANT_PERMS.has(n)).length;
  const meaningful = Array.from(perms).filter((p) => !AUTO_GRANT_PERMS.has(p));
  if (meaningful.length >= total) return "Full administrative authority.";

  const levels = permissionsToCardLevels(perms);
  const buckets: Record<PageCardLevel, string[]> = { none: [], view: [], edit: [], full: [] };
  for (const card of PAGE_CARDS) {
    const lvl = levels[card.id] ?? "none";
    if (lvl === "none") continue;
    buckets[lvl].push(card.label);
  }
  const order: PageCardLevel[] = ["full", "edit", "view"];
  const labelMap: Record<PageCardLevel, string> = {
    none: "None",
    view: "View",
    edit: "Edit",
    full: "Full",
  };
  const parts: string[] = [];
  for (const lvl of order) {
    if (buckets[lvl].length === 0) continue;
    parts.push(`${labelMap[lvl]}: ${buckets[lvl].join(", ")}`);
  }
  return parts.length > 0 ? parts.join(" · ") : "No admin access (member-level).";
}

/** Self-test invariant: every preset role's permission set must round-trip
 *  cleanly through (perms → cardLevels → perms). Run once at module load
 *  in dev to catch drift between the catalog and this map. */
export function verifyPageMapRoundTrip(): { ok: boolean; failures: string[] } {
  const failures: string[] = [];
  const presetKeys: PresetRoleKey[] = [
    "member",
    "md_admin",
    "c_suite",
    "general_admin",
    "hr_admin",
    "it_admin",
    "tech_admin",
    "super_admin",
  ];
  // dev_admin is intentionally skipped — it has every permission as a hard-
  // coded keyholder and is hidden from the UI; it does not need to round-trip.
  for (const key of presetKeys) {
    const original = new Set<string>(LEGACY_PRESET_PERMISSIONS[key]);
    const cardLevels = permissionsToCardLevels(original);
    const reconstructed = new Set<string>(cardLevelsToPermissions(cardLevels));

    const missing: string[] = [];
    const extra: string[] = [];
    original.forEach((p) => { if (!reconstructed.has(p)) missing.push(p); });
    reconstructed.forEach((p) => { if (!original.has(p)) extra.push(p); });

    if (missing.length > 0 || extra.length > 0) {
      failures.push(
        `${key}: missing=[${missing.join(",")}] extra=[${extra.join(",")}]`,
      );
    }
  }
  return { ok: failures.length === 0, failures };
}

// Run the self-test in development. We deliberately avoid throwing — drift
// is logged as a warning so the UI still loads, and the architect / smoke
// pass surfaces it clearly.
if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
  const result = verifyPageMapRoundTrip();
  if (result.ok) {
    // eslint-disable-next-line no-console
    console.log("[Role Page Map] round-trip OK for all preset roles.");
  } else {
    // eslint-disable-next-line no-console
    console.warn(
      "[Role Page Map] round-trip drift detected:\n  " + result.failures.join("\n  "),
    );
  }
}
